import { MessagePayload } from "@/shared/webworker";

import { GeoMessagePath } from "../geoTypes";

export const SOURCE = "MapDataService";

export enum MapDataCommand {
  Init = "init",
  LoadPathData = "load-path-data",
  ReleaseLoad = "release-load",
}

export enum MapDataCommandResponse {
  /** Transferable coordinate data for a path segment */
  PathCoordinates = "path-coordinates",
  /** All data for a path has been sent */
  PathDataComplete = "path-data-complete",
}

export interface LoadPathDataPayload {
  pathId: string;
  /**
   * Identifies one load of the path's coordinates. The worker tags the load's
   * coordinates and completion with it, so the panel can tell an earlier load
   * of the same path from the current one, and keeps the load's reader until
   * a {@link MapDataCommand.ReleaseLoad} names it.
   */
  loadId: string;
  topicId: string;
  latMessagePath: GeoMessagePath;
  lonMessagePath: GeoMessagePath;
  latMessagePathId: string;
  lonMessagePathId: string;
}

/**
 * Transferable coordinate buffers sent from worker to main thread.
 * These are transferred (not cloned) for zero-copy efficiency.
 */
export interface PathCoordinatesPayload {
  pathId: string;
  loadId: string;
  /** Latitude values (Float64Array transferred) */
  latitudes: Float64Array;
  /** Longitude values (Float64Array transferred) */
  longitudes: Float64Array;
  /** Log timestamps in nanoseconds (BigUint64Array transferred) */
  logTimes: BigUint64Array;
  /** Offset into the full path (for incremental streaming) */
  startIndex: number;
}

export interface MapDataCommandPayloadMap {
  [MapDataCommand.Init]: undefined;
  [MapDataCommand.LoadPathData]: LoadPathDataPayload;
  [MapDataCommand.ReleaseLoad]: { loadId: string };
}

export interface MapDataCommandResponsePayloadMap {
  [MapDataCommandResponse.PathCoordinates]: PathCoordinatesPayload;
  [MapDataCommandResponse.PathDataComplete]: {
    pathId: string;
    loadId: string;
    totalCount: number;
  };
}

export function isInitCommand(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<MapDataCommand, MapDataCommandPayloadMap[MapDataCommand.Init]>
> {
  return event.data.type === MapDataCommand.Init;
}

export function isLoadPathDataCommand(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<
    MapDataCommand,
    MapDataCommandPayloadMap[MapDataCommand.LoadPathData]
  >
> {
  return event.data.type === MapDataCommand.LoadPathData;
}

export function isReleaseLoadCommand(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<
    MapDataCommand,
    MapDataCommandPayloadMap[MapDataCommand.ReleaseLoad]
  >
> {
  return event.data.type === MapDataCommand.ReleaseLoad;
}

export function isPathCoordinatesResponse(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<
    MapDataCommandResponse,
    MapDataCommandResponsePayloadMap[MapDataCommandResponse.PathCoordinates]
  >
> {
  return event.data.type === MapDataCommandResponse.PathCoordinates;
}

export function isPathDataCompleteResponse(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<
    MapDataCommandResponse,
    MapDataCommandResponsePayloadMap[MapDataCommandResponse.PathDataComplete]
  >
> {
  return event.data.type === MapDataCommandResponse.PathDataComplete;
}
